/*
 * Copyright (c) 2021 OTB Africa <https://otbafrica.com>
 *
 * Created Date: Friday, October 15th 2021, 12:05:11 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/**
 *
 * Use this hook when you want to mutate data on the backend i.e. post, put, patch or delete.
 *
 * @param apiServicePath The API Service to call
 * @param apiQueryParams The query parameters for the API request
 * @param body The json body for the API POST request
 * @param dependencyList The list of items that should result in any useEffect hooks running during a re-render.
 * @returns array of items, count and makeRequest hook state i.e. isMakingAPIRequest and isAPIError
 *
 */
import React from "react";
import { useAPIRequest, UseAPIRequestArgs, APIMakeRequestFncArgs } from "lib/api/hooks";

export interface UseAPIMutationArgs<T> extends Omit<UseAPIRequestArgs<T>, "makeRequestOnInitialMount"> {}

export const useAPIMutation = <T>(args: UseAPIMutationArgs<T>) => {
  const { makeRequest, makeRequestCleanUp, ...othermakeRequestHookValues } = useAPIRequest<T>({
    ...args,
    makeRequestOnInitialMount: false, // Do not forsee a situation where we want to call unsafe http methods automatically on mounting. There needs to be some initiated process. If a use case ever arises, this can be changed to be an optional parameter with a default value of false.
  });

  const [isAPIDataUpdate, setUpdateState] = React.useState<boolean>(false);

  const postData = React.useCallback(
    (args: APIMakeRequestFncArgs<T>) => {
      makeRequest({ ...args, method: "POST" });
      setUpdateState(false);
    },
    [makeRequest]
  );

  const putData = React.useCallback(
    (args: APIMakeRequestFncArgs<T>) => {
      makeRequest({ ...args, method: "PUT" });
      setUpdateState(true);
    },
    [makeRequest]
  );

  const patchData = React.useCallback(
    (args: APIMakeRequestFncArgs<T>) => {
      makeRequest({ ...args, method: "PATCH" });
      setUpdateState(true);
    },
    [makeRequest]
  );

  const deleteData = React.useCallback(
    (args: APIMakeRequestFncArgs<T>) => {
      makeRequest({ ...args, method: "DELETE" });
    },
    [makeRequest]
  );

  return {
    postData,
    putData,
    patchData,
    deleteData,
    isAPIDataUpdate,
    ...othermakeRequestHookValues,
  };
};
